import { useEffect, useRef, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { SignaturePad } from "../../components/SignaturePad";
import { HOUSING_OPTIONS } from "./calc";
import { Panel, Field, Input, Select } from "./parts";

const todayKey = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const toUK = (iso) =>
  iso && /^\d{4}-\d{2}-\d{2}$/.test(iso) ? iso.split("-").reverse().join("-") : "";

const monthsBetween = (fromISO, toISO) => {
  if (!fromISO || !toISO) return null;
  const a = new Date(fromISO + "T12:00:00");
  const b = new Date(toISO + "T12:00:00");
  if (!Number.isFinite(+a) || !Number.isFinite(+b) || a > b) return null;
  const months =
    (b.getFullYear() - a.getFullYear()) * 12 +
    b.getMonth() -
    a.getMonth() -
    (b.getDate() < a.getDate() ? 1 : 0);
  return months;
};

// Compute whether the applicant's time at current address covers 3 years. If
// not, how far back we need previous addresses to reach, and whether the
// supplied previous-address intervals cover the remaining period.
const addressCoverage = (sinceISO, prevs) => {
  const today = todayKey();
  const todayDate = new Date(today + "T12:00:00");
  const cutoff = new Date(todayDate);
  cutoff.setFullYear(cutoff.getFullYear() - 3);
  if (!sinceISO)
    return { needPrevious: false, message: "Enter your move-in date to check the three-year address history.", duration: "" };
  const move = new Date(sinceISO + "T12:00:00");
  if (!Number.isFinite(+move))
    return { needPrevious: false, message: "Enter a valid move-in date.", duration: "" };
  if (move > todayDate)
    return { needPrevious: false, message: "Move-in date cannot be in the future.", duration: "" };
  const m = monthsBetween(sinceISO, today) || 0;
  const duration = `At current address: ${Math.floor(m / 12)} years, ${m % 12} months.`;
  const needPrevious = move > cutoff;
  if (!needPrevious) return { needPrevious: false, message: "", duration };
  if (!prevs || prevs.length === 0)
    return {
      needPrevious: true,
      duration,
      message: `Address history is incomplete or has gaps. Add addresses and dates back to ${cutoff.toLocaleDateString("en-GB")}.`,
    };
  const complete = prevs.every(
    (p) =>
      p.address.trim() &&
      /^\d{4}-\d{2}-\d{2}$/.test(p.from) &&
      /^\d{4}-\d{2}-\d{2}$/.test(p.to) &&
      new Date(p.from) <= new Date(p.to)
  );
  if (!complete)
    return {
      needPrevious: true,
      duration,
      message: `Address history is incomplete or has gaps. Add addresses and dates back to ${cutoff.toLocaleDateString("en-GB")}.`,
    };
  const sorted = [...prevs].sort((a, b) => new Date(b.to) - new Date(a.to));
  let edge = +move;
  for (const p of sorted) {
    if (+new Date(p.to) >= edge - 86_400_000 && +new Date(p.from) <= edge) {
      edge = Math.min(edge, +new Date(p.from));
    }
  }
  if (edge <= +cutoff)
    return { needPrevious: true, duration, message: "Three-year address period covered by the entered dates." };
  return {
    needPrevious: true,
    duration,
    message: `Address history is incomplete or has gaps. Add addresses and dates back to ${cutoff.toLocaleDateString("en-GB")}.`,
  };
};

const ApplicantForm = ({ n, data, onChange }) => {
  const update = (field, value) => onChange({ ...data, [field]: value });
  const addPrev = () =>
    update("previous", [
      ...(data.previous || []),
      { id: Math.random().toString(36).slice(2), address: "", from: "", to: "" },
    ]);
  const removePrev = (id) =>
    update(
      "previous",
      (data.previous || []).filter((p) => p.id !== id)
    );
  const updatePrev = (id, field, value) =>
    update(
      "previous",
      (data.previous || []).map((p) => (p.id === id ? { ...p, [field]: value } : p))
    );

  const coverage = addressCoverage(data.since, data.previous || []);

  return (
    <Panel
      title={`Applicant ${n}`}
      testid={`calc-applicant-${n}`}
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <Field label="First name">
          <Input
            value={data.first}
            onChange={(e) => update("first", e.target.value)}
            testid={`calc-first${n}`}
          />
        </Field>
        <Field label="Surname">
          <Input
            value={data.last}
            onChange={(e) => update("last", e.target.value)}
            testid={`calc-last${n}`}
          />
        </Field>
        <Field label="Date of birth" wide>
          <Input
            type="date"
            value={data.dob}
            max={todayKey()}
            onChange={(e) => update("dob", e.target.value)}
            testid={`calc-dob${n}`}
          />
        </Field>
        <Field label="Current housing status" wide>
          <Select
            value={data.housing}
            onChange={(e) => update("housing", e.target.value)}
            testid={`calc-housing${n}`}
          >
            <option value="">Select status</option>
            {HOUSING_OPTIONS.map((h) => (
              <option key={h}>{h}</option>
            ))}
          </Select>
        </Field>
        {data.housing === "Other" && (
          <Field label="Other housing status" wide>
            <Input
              value={data.housingOther}
              onChange={(e) => update("housingOther", e.target.value)}
            />
          </Field>
        )}
        <Field label="Current address — building / street" wide>
          <Input
            value={data.address}
            onChange={(e) => update("address", e.target.value)}
          />
        </Field>
        <Field label="Town / city">
          <Input value={data.city} onChange={(e) => update("city", e.target.value)} />
        </Field>
        <Field label="Postcode / postal code">
          <Input
            value={data.postcode}
            onChange={(e) => update("postcode", e.target.value)}
          />
        </Field>
        <Field label="Country" wide>
          <Input
            value={data.country}
            onChange={(e) => update("country", e.target.value)}
          />
        </Field>
        <Field
          label="Living here since"
          help={coverage.duration || "Enter your move-in date to check the three-year address history."}
          wide
        >
          <Input
            type="date"
            value={data.since}
            max={todayKey()}
            onChange={(e) => update("since", e.target.value)}
          />
        </Field>
      </div>

      {coverage.needPrevious && (
        <div className="pt-4 border-t border-forest/10 space-y-4">
          <p className="text-[12.5px] text-ink/70 leading-relaxed">{coverage.message}</p>
          {(data.previous || []).map((p) => (
            <div
              key={p.id}
              className="bg-cream-3 border border-forest/10 p-4 space-y-3"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Previous full address (include town, postcode and country)" wide>
                  <Input
                    value={p.address}
                    onChange={(e) => updatePrev(p.id, "address", e.target.value)}
                  />
                </Field>
                <Field label="From">
                  <Input
                    type="date"
                    value={p.from}
                    onChange={(e) => updatePrev(p.id, "from", e.target.value)}
                  />
                </Field>
                <Field label="To">
                  <Input
                    type="date"
                    value={p.to}
                    onChange={(e) => updatePrev(p.id, "to", e.target.value)}
                  />
                </Field>
              </div>
              <button
                type="button"
                onClick={() => removePrev(p.id)}
                className="inline-flex items-center gap-2 text-[12px] text-forest/70 hover:text-red-700 transition-colors"
              >
                <Trash2 size={13} /> Remove address
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={addPrev}
            className="inline-flex items-center gap-2 text-[12px] font-bold tracking-[0.14em] uppercase text-forest hover:text-gold-dark transition-colors"
          >
            <Plus size={14} /> Add previous address
          </button>
        </div>
      )}

      <SignatureBlock n={n} data={data} onChange={onChange} />
    </Panel>
  );
};

const SignatureBlock = ({ n, data, onChange }) => {
  const ref = useRef(null);
  const sigName =
    [data.first, data.last].filter(Boolean).join(" ") || `Applicant ${n}`;
  const clear = () => {
    ref.current?.clear();
    onChange({ ...data, signature: "" });
  };
  return (
    <div className="pt-4 border-t border-forest/10">
      <label className="block text-[11px] tracking-[0.14em] uppercase font-bold text-forest/70 mb-2">
        Signature — {sigName}
      </label>
      <SignaturePad
        ref={ref}
        value={data.signature || ""}
        onEnd={(dataUrl) => onChange({ ...data, signature: dataUrl })}
        testid={`calc-sig${n}`}
      />
      <div className="flex gap-3 mt-3">
        <button
          type="button"
          onClick={clear}
          className="text-[11px] font-bold tracking-[0.14em] uppercase text-forest/70 hover:text-forest transition-colors"
        >
          Clear signature
        </button>
      </div>
    </div>
  );
};

export const ClientDetails = ({ state, setState }) => {
  const setApplication = (type) => setState((s) => ({ ...s, applicationType: type }));
  const updateApp = (n, next) =>
    setState((s) => ({ ...s, [`applicant${n}`]: next }));

  return (
    <>
      <Panel title="Client details" testid="calc-client-details">
        <Field label="Application type">
          <Select
            value={state.applicationType}
            onChange={(e) => setApplication(e.target.value)}
            testid="calc-application-type"
          >
            <option value="single">Single applicant</option>
            <option value="joint">Joint applicants</option>
          </Select>
        </Field>
        <p className="text-[12px] text-sage">
          Client details remain in this browser session and are included in your
          PDF. No automatic saving.
        </p>
      </Panel>

      <ApplicantForm
        n={1}
        data={state.applicant1}
        onChange={(next) => updateApp(1, next)}
      />
      {state.applicationType === "joint" && (
        <ApplicantForm
          n={2}
          data={state.applicant2}
          onChange={(next) => updateApp(2, next)}
        />
      )}
    </>
  );
};

export const emptyApplicant = () => ({
  first: "",
  last: "",
  dob: "",
  housing: "",
  housingOther: "",
  address: "",
  city: "",
  postcode: "",
  country: "United Kingdom",
  since: "",
  previous: [],
  signature: "",
});
